import { ReactElement, useEffect, useState } from "react";
//import HorizontalLayout from "../common/HorizontalLayout";
import VerticalLayout, { IVerticalSplitterState } from "../common/VerticalLayout";
import styles from "./CodeReview.module.css";
import useApiState, { ApiStateSetter } from "../../hooks/use-api-state";
import Loading from "../common/Loading";
import { IAppInfo } from "../../messages/IAppInfo";
import { IModule, LabAccomplishment } from "../../messages/IGetUserContentResponse";
import { NavLink } from "react-router-dom";

let retryTimeout: NodeJS.Timeout | null = null;

export default function CodeReview(props: {
  appInfo: IAppInfo,
  userId: string,
  cohortCode: string,
  contentId: string,
  chapterId: string,
  module: IModule,
  labAccomplishment?: LabAccomplishment|null,
  previousLessonLink?: string | null,
  nextLessonLink?: string | null,
  onSubmitted: ( index: number,
                 //setMessage: ((message: string | null) => void) | null,
                 setSubmitApiState: ApiStateSetter 
               ) => void,
  codeReviewOnly: boolean,
  message: string | null,
}): ReactElement {
  const [submitApiState, setSubmitApiState] = useApiState(false);
  const [message, setMessage] = useState<string|null>(props.message ?? null);
  const [selectedLine, setSelectedLine] = useState<number>(-1);
  const [lessonButtonsMargin, setLessonButtonsMargin] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [retryIn, setRetryIn] = useState<string|null>(null);

  useEffect(() => {
    labcontrol_init(props.appInfo.gcs_address, props.userId, props.appInfo.csrf_token);
    reset();
    setMessage(props.message);
  }, [props.appInfo, props.userId, props.labAccomplishment]);


  useEffect(() => {
    reset();
  }, [props.cohortCode, props.contentId, props.chapterId, props.module]);


  function reset() {
    setFinished(false);
    setSelectedLine(-1);
    setRetryIn(null);
  }


  function repositionChangeLessonButtons() {
    setLessonButtonsMargin(10);
  }


  function handleSplitterChange(state: IVerticalSplitterState) {
    repositionChangeLessonButtons();
  }


  function checkDone() {
    if (retryTimeout)
      clearTimeout(retryTimeout);
    //console.log(`checkDone: labAccomplishment=${JSON.stringify(props.labAccomplishment)}`);
    if (props.labAccomplishment) {
      let finished = props.labAccomplishment.finished ?? false;
      setFinished(finished);
      setSelectedLine(finished ? props.labAccomplishment.line_marked ?? -1 : -1);
      //console.log(`-finished=${finished} status=${props.labAccomplishment.status} started=${props.labAccomplishment.started} codereview_retry_minutes=${props.labAccomplishment.codereview_retry_minutes}`);
      if (finished && !statusCorrect(props.labAccomplishment.status) && props.labAccomplishment.started && props.labAccomplishment.started>0) { // Check if redoable
        const now = Math.floor(Date.now() / 1000);
        const elapsed = now - props.labAccomplishment.started;
        //console.log(`now=${now} started=${props.labAccomplishment.started} elapsed=${elapsed}`);
        if (elapsed > props.labAccomplishment.codereview_retry_minutes*60) {
          reset();
        }
        else { // Indicate when it can be redone
          let left = props.labAccomplishment.codereview_retry_minutes*60 - elapsed;
          let hours = Math.floor(left/3600);
          let minutes = Math.floor((left - hours*3600)/60);
          let seconds = left - hours*3600 - minutes*60;
          //console.log(`hours=${hours} minutes=${minutes} seconds=${seconds}`);
          if (hours>0) {
            setRetryIn(`You can retry it in ${hours} hour${hours>1?'s':''}.`);
          }
          else if (minutes>0) {
            setRetryIn(`You can retry it in ${minutes+1} minute${(minutes+1)>1?'s':''}.`);
            retryTimeout = setTimeout(checkDone, (seconds+1)*1000);
          }
          else if (seconds>0) {
            setRetryIn(`You can retry it in ${seconds} second${seconds>1?'s':''}.`);
            retryTimeout = setTimeout(checkDone, 1000);
          }
          else {
            reset();
          }
        }
      }
      else  {
        setRetryIn(null);
      }
    }
  }


  useEffect(() => {
    //console.log(`useEffect: labAccomplishment=${JSON.stringify(props.labAccomplishment)}`);
    checkDone();
  }, [props.labAccomplishment]);

/*
  useEffect(() => {
    console.log(`selectedLine changed: ${selectedLine}`);
  }, [selectedLine]);
*/

  // Since both "correct" and "partial" are considered as success, we use this helper
  function statusCorrect(status:string): boolean {
    return (status==="correct") || (status==="partial");
  }


  function getLineStyle(index: number): string {
    let style = `${styles.code_line}`;
    if (!finished) {
      if (index===selectedLine)
        style += ` ${styles.selected_line}`;
    }
    else { // finished
      if (props.labAccomplishment)
      {
        //console.log(`-line=${index}(${typeof index}) marked=${props.labAccomplishment.line_marked}(${typeof props.labAccomplishment.line_marked}) ${index===props.labAccomplishment.line_marked} status=${props.labAccomplishment.status}`);
        if (index===props.labAccomplishment.line_marked) {
          if (statusCorrect(props.labAccomplishment.status))
            style += ` ${styles.correct_line}`;
          else
            style += ` ${styles.failed_line}`;
        }
      }
    }
    return style;
  }

  return <>
    <div className={styles.messageDiv}>
      {message &&
        <div className={styles.message}>
            {message}
        </div>
      }
      {finished ?
        <div className={styles.instructions}>
          {retryIn && <div>{retryIn}</div>}
        </div>
      : <div className={styles.instructions}>
          {props.module.instructions}
        </div>
      }
    </div>

    <VerticalLayout
      keyForStorage="codeReview"
      onSplitterChange={handleSplitterChange}
      topPanelClassName={styles.code_review_top_panel}
      topPanel={        
        <table className={styles.code_lines_table}>
          <tbody className={styles.code_lines_table_body}>
          {!!props.module.lines && props.module.lines.map((line: string, index) => {
            return (
              <tr key={index} className={getLineStyle(index)} 
                  onClick={(e) => { 
                    if (!finished) {
                      setSelectedLine(index===selectedLine ? -1 : index); }
                      }
                    }>
                <td className={styles.line_status_icon}>
                  {props.labAccomplishment && finished && index===props.labAccomplishment.line_marked && statusCorrect(props.labAccomplishment.status) &&
                    <img src="/icons/OK_40x40_2.png"
                         className={styles.bug_icon}
                         alt=""
                    />
                  }
                  {props.labAccomplishment && finished && index===props.labAccomplishment.line_marked && !statusCorrect(props.labAccomplishment.status) &&
                    <img src="/icons/error_40x40.png"
                         className={styles.bug_icon}
                         alt=""
                    />
                  }
                  {index===selectedLine && !finished &&
                    <img src="/icons/bug_40x40_2.png"
                         className={styles.bug_icon}
                         alt=""
                    />
                  }
                </td>
                <td className={styles.line_number}>{index+1}</td>
                <td className={styles.line_content}>{line}</td>
              </tr>);
            })
          }
          </tbody>
        </table>
      }
      bottomPanel={
        <>
          <div className={styles.bottom_panel}>
            <div className={styles.change_lesson_controls}>
              {props.codeReviewOnly ? ( <div></div> ) : (
                <button className={`btn narrow primary ${styles.change_lesson_button}`}
                        style={{ marginLeft: lessonButtonsMargin }}>
                  <NavLink to={props.previousLessonLink ?? ""}>Previous lesson</NavLink>
                </button>
              )}
              <div className={styles.submit_buttons_div}>
                <Loading isLoading={submitApiState.isLoading} className={styles.submit_loading}>
                  <button className={`btn narrow secondary ${styles.submit_button}`}
                      disabled={finished || selectedLine < 0}
                      onClick={(e) => {
                        if (selectedLine >= 0) {
                          props.onSubmitted(selectedLine, /*setMessage,*/ setSubmitApiState);
                        }
                      }}
                  >Submit</button>
                </Loading>
              </div>
              {props.codeReviewOnly ? ( <div></div> ) : (
                <button className={`btn narrow primary ${styles.change_lesson_button}`}
                        style={{ marginRight: lessonButtonsMargin }}>
                  <NavLink to={props.nextLessonLink ?? ""}>Next lesson</NavLink>
                </button>
              )}
            </div>
            {message &&
              <div className={styles.messageEnd}>
                  {message}
              </div>
            }
          </div>
        </>
      }
    />
  </>
}
